import { useState, useCallback, useEffect } from "react";
import Header from "../../components/header";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/common/modal";
import Toast from "../../components/common/toast";
import useAuthStore from "../../store/authStore";
import userHooks from "../../hooks/userHooks";
import { calculatePregnancyInfo } from "../../utils/pregnancyUtils";
import ChevronRight from "../../images/Chevron_Right.png";

const Mypage = () => {
  const navigate = useNavigate();

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userInfo = useAuthStore((state) => state.userInfo);
  const logout = useAuthStore((state) => state.logout);

  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isToastOpen, setIsToastOpen] = useState(false);

  const [isMessageAllow, setIsMessageAllow] = useState(userInfo?.message_allow);

  const pregnancyInfo = calculatePregnancyInfo(userInfo?.baby?.[0]?.baby_birth);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const updateLocalStorage = useCallback((newMessageAllow) => {
    const currentStorage = localStorage.getItem(
      "dplanit-moming-auth-storage"
    );
    if (currentStorage) {
      const storageData = JSON.parse(currentStorage);
      const newState = {
        ...storageData.state,
        userInfo: {
          ...storageData.state.userInfo,
          message_allow: newMessageAllow,
        },
      };

      const newStorageData = {
        state: newState,
        version: 0,
      };
      localStorage.setItem(
        "dplanit-moming-auth-storage",
        JSON.stringify(newStorageData)
      );
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      // eslint-disable-next-line no-unused-vars
      const response = await logout();

      navigate("/");
    } catch (error) {
      console.error(error);
      setIsToastOpen(true);
    }
  }, [logout, navigate]);

  const handleSubscribeNotifications = useCallback(async () => {
    try {
      const response = await userHooks.updateMessageAllowHooks({
        messageAllow: true,
      });

      if (response.IsError) {
        setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
        setIsToastOpen(true);
        return;
      }

      setIsMessageAllow(true);
      updateLocalStorage(true);
      setToastMessage("신청이 완료되었습니다. 매 주수 별 1일차에 알림톡이 전송됩니다.");
      setIsToastOpen(true);

      setTimeout(() => {
        window.location.reload();
      }, 4000);
    } catch (error) {
      console.error(error);
      setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
      setIsToastOpen(true);
    }
  }, [updateLocalStorage]);

  const handleBlockNotifications = useCallback(async () => {
    try {
      const response = await userHooks.updateMessageAllowHooks({
        messageAllow: false,
      });

      if (response.IsError) {
        setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
        setIsToastOpen(true);
        return;
      }

      setIsMessageAllow(false);
      updateLocalStorage(false);
      setToastMessage("알림톡 & 전문가 상담이 차단되었습니다.");
      setIsToastOpen(true);
      setIsBlockModalOpen(false);

      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error(error);
      setToastMessage("오류가 발생했습니다. 다시 시도해주세요.");
      setIsToastOpen(true);
    }
  }, [updateLocalStorage]);

  const handleMessageAllowClick = useCallback(() => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    
    if (isMessageAllow) {
      setIsBlockModalOpen(true);
    } else {
      handleSubscribeNotifications();
    }
  }, [handleSubscribeNotifications, isMessageAllow, isAuthenticated, navigate]);

  const handleCloseModal = useCallback(() => {
    setIsBlockModalOpen(false);
  }, []);

  const handleCloseLogoutModal = useCallback(() => {
    setIsLogoutModalOpen(false);
  }, []);

  const handleCloseToast = useCallback(() => {
    setIsToastOpen(false);
  }, []);

  const MoveToQnA = useCallback(() => {
    window.open("http://pf.kakao.com/_xfDPbs", "_blank", "noopener,noreferrer,nofollow");
  }, []);

  return (
    <div
      className="flex flex-col bg-white pt-[3.125rem] md:pt-[4.375rem]"
      style={{ 
        minHeight: "calc(var(--vh, 1vh) * 100)",
        paddingBottom: "max(1.875rem, env(safe-area-inset-bottom))"
      }}
    >
      <Header selectedMenu={3} />
      <div className="flex-1 pt-[1.875rem] md:pt-[3.125rem] px-[1.4375rem] md:px-[4.3125rem] flex flex-col">
        <div className="mb-[0.8125rem] md:mb-[1.5rem]">
          <div className="flex mb-[1rem]">
            <span className="text-[0.9375rem] md:text-[1.125rem] font-semibold text-[#FF931D]">
              내 아기 정보
            </span>
          </div>
          {!isAuthenticated ? (
            <div className="flex flex-col gap-[0.375rem] md:gap-[0.625rem]">
              <div className="flex justify-between items-center">
                <span className="text-[0.9375rem] md:text-[1.125rem] text-[#222]">
                  아기정보를 입력해보세요.
                </span>
                <button
                  className="px-[0.4375rem] py-[0.1875rem] md:px-[0.625rem] rounded-full border border-[#CCCCCC] text-[0.625rem] md:text-[0.9375rem] text-[#888888] font-medium"
                  onClick={() => navigate("/")}
                >
                  정보 입력
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-[0.375rem] md:gap-[0.625rem]">
              <div className="flex justify-between items-center h-[1.0625rem]">
                <div className="space-x-[0.3125rem] flex items-center">
                  <span className="text-[0.9375rem] md:text-[1.125rem] font-bold text-[#222]">
                    {userInfo?.baby[0]?.baby_name || ""}
                  </span>
                  <span className="text-[0.9375rem] md:text-[1.125rem] text-[#888888]">
                    {pregnancyInfo.isPostpartum
                      ? `출산 후 ${pregnancyInfo.daysSinceBirth}일`
                      : `임신 ${pregnancyInfo.week}주차`}
                  </span>
                </div>
                <button
                  className="px-[0.4375rem] py-[0.1875rem] md:px-[0.625rem] rounded-full border border-[#CCCCCC] text-[0.625rem] md:text-[0.9375rem] text-[#888888] font-medium"
                  onClick={() => navigate("/mypage/baby-info")}
                >
                  정보 수정
                </button>
              </div>
              <div className="text-[0.625rem] md:text-[0.8125rem] text-[#888888]">
                {pregnancyInfo.isPostpartum
                  ? "아기와 만나셨군요! 축하드립니다 :)"
                  : "아기를 좀 일찍 만나셨다면, 아기 정보를 수정해주세요 :)"}
              </div>
            </div>
          )}
        </div>

        <div className="mb-[1.875rem] md:mb-[2.5rem]">
          <div className="text-[0.9375rem] md:text-[1.125rem] font-semibold text-[#FF931D] mb-[0.625rem]">
            정보 변경
          </div>
          <div className="flex flex-col gap-[0.625rem]">
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={() => navigate("/mypage/my-info")}
            >
              내 전화번호 변경
            </button>
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={handleMessageAllowClick}
            >
              {isMessageAllow
                ? "알림톡 & 전문가 상담 차단"
                : "알림톡 & 전문가 상담 신청"}
            </button>
          </div>
        </div>

        <div className="mb-[1.875rem] md:mb-[2.5rem]">
          <div className="text-[0.9375rem] md:text-[1.125rem] font-semibold text-[#FF931D] mb-[0.625rem]">
            고객센터
          </div>
          <div className="flex flex-col gap-[0.625rem]">
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={MoveToQnA}
            >
              카카오 상담
            </button>
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={() => (window.location.href = "tel:070-8805-3009")}
            >
              전화 상담
            </button>
          </div>
        </div>

        <div>
          <div className="text-[0.9375rem] md:text-[1.125rem] font-semibold text-[#FF931D] mb-[0.625rem]">
            약관 및 정책
          </div>
          <div className="flex flex-col gap-[0.625rem]">
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={() =>
                window.open(
                  "https://www.notion.so/1fba60b643c7818b8bf4f9e6fb162cb7",
                  "_blank"
                )
              }
            >
              서비스 이용 약관
            </button>
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={() =>
                window.open(
                  "https://www.notion.so/1fba60b643c78159881cf8a8f011d903",
                  "_blank"
                )
              }
            >
              개인 정보 처리 방침
            </button>
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={() =>
                window.open(
                  "https://www.notion.so/1fba60b643c7815d9649d76abd122940",
                  "_blank"
                )
              }
            >
              개인 민감 정보 처리 방침
            </button>
            <button
              className="text-[0.9375rem] md:text-[1.125rem] text-[#222] text-left"
              onClick={() =>
                window.open(
                  "https://www.notion.so/1fba60b643c7818396b8ffb4cfda3b6c",
                  "_blank"
                )
              }
            >
              마케팅 정보 수신
            </button>
          </div>
        </div>

        {isAuthenticated && (
          <div className="flex justify-center items-center mt-[6.375rem]">
            <button
              className="inline-flex items-center justify-center gap-[0.25rem] mx-auto"
              onClick={() => setIsLogoutModalOpen(true)}
            >
              <span className="text-[0.875rem] md:text-[1.1875rem] font-bold leading-none tracking-normal text-center align-middle text-[#B6B7B9]">로그아웃</span>
              <img 
                src={ChevronRight} 
                alt="chevron right" 
                className="w-[0.875rem] h-[0.875rem] md:w-[1.125rem] md:h-[1.125rem]"
              />
            </button>
          </div>
        )}
      </div>

      <Modal isOpen={isBlockModalOpen} onClose={handleCloseModal}>
        <div className="p-[1.5625rem]">
          <div className="text-[1rem] font-normal">
            알림톡&전문가 상담 기능을
            <br />
            차단 하시겠습니까?
          </div>
        </div>
        <div className="flex border-t border-[#eee]">
          <button
            className="flex-1 py-[1.1875rem] font-normal border-r border-[#eee]"
            onClick={handleCloseModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-3 font-bold"
            onClick={handleBlockNotifications}
          >
            차단
          </button>
        </div>
      </Modal>

      <Modal isOpen={isLogoutModalOpen} onClose={handleCloseLogoutModal}>
        <div className="p-[1.5625rem]">
          <div className="text-[1rem] font-normal">로그아웃 하시겠습니까?</div>
        </div>
        <div className="flex border-t border-[#eee]">
          <button
            className="flex-1 py-[1.1875rem] font-normal border-r border-[#eee]"
            onClick={handleCloseLogoutModal}
          >
            아니요
          </button>
          <button className="flex-1 py-3 font-bold" onClick={handleLogout}>
            예
          </button>
        </div>
      </Modal>

      <Toast
        message={toastMessage}
        isOpen={isToastOpen}
        onClose={handleCloseToast}
      />
    </div>
  );
};

export default Mypage;
